/**
 * LangMenu — flag-based language switcher for the public marketing header.
 *
 * Shows ONE flag (the current language). Click → a dropdown "mega-menu" listing every available
 * language as flag + its native name. Built so adding a language later is one line in LANGS (+ its
 * locale registered in i18n/index.js). Closes on outside-click / Escape / selection. Choice persists
 * under LANG_STORAGE_KEY, same as the rest of the app.
 */
import React, { useEffect, useRef, useState } from 'react';
import i18n, { LANG_STORAGE_KEY } from '../i18n';
import usFlag from '../assets/flags/us.svg';
import thFlag from '../assets/flags/th.svg';
import khFlag from '../assets/flags/kh.svg';
import laFlag from '../assets/flags/la.svg';
import myFlag from '../assets/flags/my.svg';

// One entry per language whose locale is registered in i18n/index.js. `name` = native endonym so an
// owner recognises their own language. Add a row here (+ its locale + flag) to grow the menu.
export const LANGS = [
    { code: 'en', flag: usFlag, name: 'English' },
    { code: 'th', flag: thFlag, name: 'ไทย' },
    { code: 'km', flag: khFlag, name: 'ភាសាខ្មែរ' },
    { code: 'lo', flag: laFlag, name: 'ພາສາລາວ' },
    { code: 'ms', flag: myFlag, name: 'Bahasa Melayu' },
];

const setLang = (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem(LANG_STORAGE_KEY, code); } catch (e) { /* ignore */ }
};

const LangMenu = () => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const cur = (i18n.language || 'en').slice(0, 2);
    const active = LANGS.find(l => l.code === cur) || LANGS[0];

    // Close on outside click or Escape.
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
    }, [open]);

    const pick = (code) => { setLang(code); setOpen(false); };

    return (
        <div className='mkt-lang' ref={ref}>
            <button
                type='button'
                className='mkt-lang-btn'
                aria-haspopup='listbox'
                aria-expanded={open}
                aria-label={`Language: ${active.name}`}
                onClick={() => setOpen(o => !o)}
            >
                <img src={active.flag} alt='' className='mkt-lang-flag' />
                <svg className={`mkt-lang-caret${open ? ' up' : ''}`} width='10' height='6' viewBox='0 0 10 6' aria-hidden='true'>
                    <path d='M1 1l4 4 4-4' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
            </button>
            {open && (
                <ul className='mkt-lang-menu' role='listbox' aria-label='Choose a language'>
                    {LANGS.map(l => (
                        <li key={l.code} role='option' aria-selected={l.code === cur}>
                            <button type='button' className={`mkt-lang-item${l.code === cur ? ' on' : ''}`} onClick={() => pick(l.code)}>
                                <img src={l.flag} alt='' className='mkt-lang-flag' />
                                <span>{l.name}</span>
                                {l.code === cur && <span className='mkt-lang-tick' aria-hidden='true'>✓</span>}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default LangMenu;
